import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";

import FilterChip from "./FilterChip";

/**
 * FilterChip を横並びにしたフィルターバー（旧 `pages/home/components/TagFilterBar`
 * のグローバル化）。
 *
 * - 先頭に「すべて」チップ（`onSelect(null)` でフィルター解除）。
 *   `showAllChip={false}` で非表示にできる（常にいずれかのタグを選択させる用途）
 * - `onReorder` を渡すと各チップを D&D で並び替えできる
 * - `onAdd` を渡すと末尾に「+」チップ（インライン入力）が付き、その場で追加できる
 * - `onRemove` を渡すと各チップのタグ名後ろに ×（`ContentTag` と同じ並び）が付き、
 *   その場で削除できる
 * - `onRename` を渡すとチップのダブルクリックでインライン入力に変わり、
 *   その場で名前を変更できる
 * - 既定は横スクロール（スクロールバー非表示）。`wrap` で折り返しに切替
 *
 * 利用箇所: ホームのショットタグ、ロール管理、マイページのロール、
 * オファーウィザードの RolePicker、マイサイト作品登録（管理モード）、
 * 公開プロフィール 等。
 */
export interface FilterChipBarItem {
  id: string;
  name: string;
}

export interface FilterChipBarProps<T extends FilterChipBarItem = FilterChipBarItem> {
  items: T[];
  /** 選択中の id。`null` = すべて */
  selectedId: string | null;
  // eslint-disable-next-line no-unused-vars
  onSelect: (id: string | null) => void;
  /** 指定すると D&D 並び替えを有効化し、確定時に新しい並びを通知する */
  // eslint-disable-next-line no-unused-vars
  onReorder?: (reordered: T[]) => void;
  /**
   * 指定すると末尾に「+」チップを表示し、インライン入力で確定した名前を通知する
   * （タグマスタの管理バー用途）。items が空でもバーを描画する。
   */
  // eslint-disable-next-line no-unused-vars
  onAdd?: (name: string) => void;
  /** 指定すると各チップのタグ名後ろに × を表示し、クリックで通知する（管理バー用途） */
  // eslint-disable-next-line no-unused-vars
  onRemove?: (item: T) => void;
  /**
   * 指定するとチップのダブルクリックでインライン入力に変わり、確定した名前を
   * 通知する（管理バー用途）。名前が変わらなければ呼ばれない。
   */
  onRename?: (
    // eslint-disable-next-line no-unused-vars
    item: T,
    // eslint-disable-next-line no-unused-vars
    name: string,
  ) => void;
  /** 追加入力のプレースホルダ。既定「新しいタグ」 */
  addPlaceholder?: string;
  /** 先頭チップのラベル。既定「すべて」 */
  allLabel?: string;
  /** 先頭の「すべて」チップを表示するか。既定 true */
  showAllChip?: boolean;
  /** true で折り返しレイアウト（既定は横スクロール） */
  wrap?: boolean;
  /** チップの寄せ（既定は左寄せ）。center でもはみ出す場合は左端から並ぶ */
  align?: "start" | "center";
  className?: string;
}

const FixedChipSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const ScrollContainer = styled.div<{ $wrap: boolean; $align: "start" | "center" }>`
  width: 100%;
  display: flex;
  align-items: center;
  /* safe: 横スクロールではみ出したとき、先頭のチップが左に切れて
     スクロールで届かなくなるのを防ぐ */
  justify-content: ${({ $align }) => ($align === "center" ? "safe center" : "flex-start")};
  gap: 8px;
  ${({ $wrap }) =>
    $wrap
      ? `
  flex-wrap: wrap;
  padding: 4px 0 8px;
  `
      : `
  overflow-x: auto;
  padding: 4px 0 12px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  `}
`;

// ===== 削除（×）付きチップ =====

/**
 * チップ末尾（タグ名の後ろ）の × 削除アイコン。`ContentTag` の onRemove と
 * 同じ「テキスト + ×」の並びに統一する。管理バー（onRemove あり）でのみ表示。
 * チップ本体（選択トグル）とはクリックを分離するため stopPropagation する。
 */
const RemoveIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 14px;
  line-height: 1;
  color: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }
`;

interface RemovableChipProps {
  name: string;
  selected: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  onRemove?: () => void;
  removeLabel?: string;
}

const RemovableChip: React.FC<RemovableChipProps> = ({
  name,
  selected,
  onClick,
  onDoubleClick,
  onRemove,
  removeLabel,
}) => {
  return (
    <FixedChipSlot>
      <FilterChip
        selected={selected}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        title={onDoubleClick ? "ダブルクリックで名前を変更" : undefined}
      >
        {name}
        {onRemove && (
          <RemoveIcon
            role="button"
            aria-label={removeLabel ?? `${name} を削除`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            close
          </RemoveIcon>
        )}
      </FilterChip>
    </FixedChipSlot>
  );
};

// ===== 追加（+）チップ =====

const AddChipButton = styled.button.attrs({ className: "bodytext-13" })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px dashed var(--soremo-light-gray);
  color: var(--soremo-light-gray);
  background: transparent;
  padding: 4px 10px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: var(--soremo-blue);
    border-color: var(--soremo-blue);
  }

  .material-symbols-rounded {
    font-size: 14px;
    line-height: 1;
  }
`;

const AddInput = styled.input.attrs({ className: "bodytext-13" })`
  /* type 属性を付けず soremo_style.scss の input[type="text"] を回避し、
   * チップと同じ pill 形状のインライン入力にする。 */
  width: 120px;
  border: 1px solid var(--soremo-blue);
  border-radius: 999px;
  padding: 4px 10px;
  line-height: 1;
  outline: none;
  margin: 0;
  background: #fff;
  flex-shrink: 0;
`;

interface AddChipProps {
  placeholder: string;
  // eslint-disable-next-line no-unused-vars
  onCommit: (name: string) => void;
}

/**
 * 「+」クリックでインライン入力に切り替わる追加チップ。
 * Enter で確定（連続入力のため開いたまま）、Escape / 空欄 blur で閉じる。
 * IME 変換中の Enter では確定しない。
 */
const AddChip: React.FC<AddChipProps> = ({ placeholder, onCommit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = () => {
    const name = draft.trim().slice(0, 60);
    setDraft("");
    if (name) onCommit(name);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isComposing && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setDraft("");
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    commit();
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <AddChipButton
        type="button"
        aria-label="タグを追加"
        onClick={() => {
          setIsEditing(true);
          // マウント後にフォーカス（autoFocus だと横スクロール位置が飛ぶ場合がある）
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <span className="material-symbols-rounded" aria-hidden>
          add
        </span>
      </AddChipButton>
    );
  }

  return (
    <AddInput
      ref={inputRef}
      value={draft}
      placeholder={placeholder}
      aria-label="新しいタグ名"
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={handleKeyDown}
      onCompositionStart={() => setIsComposing(true)}
      onCompositionEnd={() => setIsComposing(false)}
      onBlur={handleBlur}
    />
  );
};

// ===== 並び替え可能チップ =====

interface SortableChipProps {
  id: string;
  name: string;
  selected: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  onRemove?: () => void;
}

const SortableChip: React.FC<SortableChipProps> = ({
  id,
  name,
  selected,
  onClick,
  onDoubleClick,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    flexShrink: 0,
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <RemovableChip
        name={name}
        selected={selected}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onRemove={onRemove}
      />
    </div>
  );
};

// ===== 名前変更（インライン入力）=====

interface RenameChipProps {
  initialName: string;
  // eslint-disable-next-line no-unused-vars
  onCommit: (name: string) => void;
  onCancel: () => void;
}

/**
 * ダブルクリックで開くリネーム入力。Enter / blur で確定、Escape で取り消し。
 * 見た目は追加チップの入力（AddInput）と揃える。
 */
const RenameChip: React.FC<RenameChipProps> = ({
  initialName,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(initialName);
  const [isComposing, setIsComposing] = useState(false);
  // 確定と blur の二重発火を防ぐ（Enter 確定後の blur を無視する）
  const settledRef = useRef(false);

  const settle = (commit: boolean) => {
    if (settledRef.current) return;
    settledRef.current = true;
    const name = draft.trim().slice(0, 60);
    if (commit && name && name !== initialName) onCommit(name);
    else onCancel();
  };

  return (
    <AddInput
      autoFocus
      value={draft}
      aria-label="名前を変更"
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !isComposing && !e.nativeEvent.isComposing) {
          e.preventDefault();
          settle(true);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          settle(false);
        }
      }}
      onCompositionStart={() => setIsComposing(true)}
      onCompositionEnd={() => setIsComposing(false)}
      onBlur={() => settle(true)}
    />
  );
};

function FilterChipBar<T extends FilterChipBarItem = FilterChipBarItem>({
  items,
  selectedId,
  onSelect,
  onReorder,
  onAdd,
  onRemove,
  onRename,
  addPlaceholder = "新しいタグ",
  allLabel = "すべて",
  showAllChip = true,
  wrap = false,
  align = "start",
  className,
}: FilterChipBarProps<T>): React.ReactElement | null {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const sortableIds = useMemo(() => items.map((t) => t.id), [items]);

  /** リネーム中のチップ id（onRename 指定時のみ使う）。 */
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((t) => t.id === active.id);
      const newIndex = items.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      onReorder?.(arrayMove(items, oldIndex, newIndex));
    },
    [items, onReorder],
  );

  const hasItems = items && items.length > 0;
  if (!hasItems && !onAdd) return null;

  const startRename = onRename
    ? (item: T) => setRenamingId(item.id)
    : undefined;

  /** リネーム中はドラッグ対象から外し、その場を入力欄に差し替える。 */
  const renderChip = (item: T) => {
    if (onRename && renamingId === item.id) {
      return (
        <RenameChip
          key={item.id}
          initialName={item.name}
          onCommit={(name) => {
            setRenamingId(null);
            onRename(item, name);
          }}
          onCancel={() => setRenamingId(null)}
        />
      );
    }
    const shared = {
      name: item.name,
      selected: selectedId === item.id,
      onClick: () => onSelect(item.id),
      onDoubleClick: startRename ? () => startRename(item) : undefined,
      onRemove: onRemove ? () => onRemove(item) : undefined,
    };
    return onReorder ? (
      <SortableChip key={item.id} id={item.id} {...shared} />
    ) : (
      <RemovableChip key={item.id} {...shared} />
    );
  };

  const chips = onReorder ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToHorizontalAxis]}
    >
      <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
        {items.map(renderChip)}
      </SortableContext>
    </DndContext>
  ) : (
    items.map(renderChip)
  );

  return (
    <ScrollContainer $wrap={wrap} $align={align} className={className}>
      {hasItems && showAllChip && (
        <FixedChipSlot>
          <FilterChip selected={selectedId === null} onClick={() => onSelect(null)}>
            {allLabel}
          </FilterChip>
        </FixedChipSlot>
      )}
      {chips}
      {onAdd && <AddChip placeholder={addPlaceholder} onCommit={onAdd} />}
    </ScrollContainer>
  );
}

export default FilterChipBar;
